import { LuIcon, Text } from "@overmap-ai/blocks"
import type { Asset, AssetTypeStatus, Stored } from "@overmap-ai/common"
import { type ComponentProps, Fragment, memo, useMemo } from "react"
import {
	type DefaultLegendContentProps,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	type TooltipContentProps,
} from "recharts"

import { modelIconMapping } from "../../mappings/icons"
import { TooltipWrapper } from "./components/TooltipWrapper"
import { ZERO_MARGIN } from "./constants"

interface AssetStatusPieChartProps extends Omit<ComponentProps<typeof ResponsiveContainer>, "children"> {
	assets: Stored<Asset>[]
	assetTypeStatuses: Stored<AssetTypeStatus>[]
	hideTooltip?: boolean
}

export const AssetStatusPieChart = memo<AssetStatusPieChartProps>((props) => {
	const { assets, assetTypeStatuses, hideTooltip, ...rest } = props

	const data = useMemo(() => {
		const countByStatus: Record<AssetTypeStatus["uuid"], number> = {}

		for (const { uuid } of assetTypeStatuses) countByStatus[uuid] = 0

		for (const { status } of assets) countByStatus[status] += 1

		return assetTypeStatuses.map((status) => {
			return {
				name: status.name,
				fill: status.color,
				count: countByStatus[status.uuid],
			}
		})
	}, [assets, assetTypeStatuses])

	return (
		<ResponsiveContainer {...rest}>
			<PieChart margin={ZERO_MARGIN} className="text-xs">
				<Pie
					className="outline-none"
					data={data}
					cx="50%"
					cy="50%"
					labelLine={false}
					innerRadius="80%"
					outerRadius="100%"
					dataKey="count"
					nameKey="name"
					stroke="var(--base-a6)"
				/>
				<Legend content={LegendComponent} itemSorter={null} />
				{!hideTooltip && <Tooltip content={TooltipContent} />}
			</PieChart>
		</ResponsiveContainer>
	)
})
AssetStatusPieChart.displayName = "AssetStatusPieChart"

function TooltipContent(props: TooltipContentProps<number, string>) {
	const { active, payload } = props

	const isVisible = active && payload && payload.length > 0

	if (!isVisible) return null

	return (
		<TooltipWrapper className="flex items-center gap-1" size="xs">
			{payload.map(({ dataKey, value, name, payload }) => {
				return (
					<Fragment key={dataKey}>
						<LuIcon icon={modelIconMapping.stage} color={payload.fill} fill={payload.fill} />
						<div className="flex grow items-center justify-between gap-2">
							<Text accentColor="base" size="sm">
								{name ?? dataKey}
							</Text>
							<Text size="sm">{value}</Text>
						</div>
					</Fragment>
				)
			})}
		</TooltipWrapper>
	)
}

function LegendComponent(props: DefaultLegendContentProps) {
	const { payload } = props

	if (!payload) return null

	return (
		<div className="mt-4 flex w-full justify-center">
			<div className="grid grid-cols-3 justify-center gap-x-4">
				{payload.map((entry, index) => (
					<div
						className="text-(--base-11) flex items-center justify-center gap-1 text-sm"
						key={`item-${index}`}
					>
						<LuIcon icon={modelIconMapping.stage} color={entry.color} fill={entry.color} />
						{entry.value}
					</div>
				))}
			</div>
		</div>
	)
}
